import { createSlice } from '@reduxjs/toolkit';

import { Dispatch } from 'redux';
import set from 'lodash/set';
import * as AuthService from 'modules/auth/services/AuthService';
import { initBenGuide } from 'modules/auth/slices/benguideSlice';

interface AuthState {
  inProgress: boolean;
  benguideError: any;
  showErrorMessage: boolean;
}

const initialState = {
  inProgress: false,
  benguideError: { authFailed: false, notPublishedBenguide: false },
  showErrorMessage: false,
} as AuthState;

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authenticateInprogress: (state) => {
      state.inProgress = true;
      state.showErrorMessage = false;
    },
    authenticateCompleted: (state) => {
      state.benguideError = null;
      state.inProgress = false;
    },
    authenticateFailed: (state, action: any) => {
      const { payload = {} } = action;
      const { response = {} } = payload;
      const { status, data } = response;
      const { code = '', error_description: errorDescription } = data || {};
      state.benguideError =
        status === 401 || status === 403
          ? { authFailed: true }
          : { authFailed: true, notFound: true };
      set(
        state,
        'benguideError.notFound',
        status === 404 ||
          errorDescription === 'benguide.deleted' ||
          code === 'archived/deleted benefit guide'
      );
      set(
        state,
        'benguideError.notPublishedBenguide',
        code === 'not a published benefit guide'
      );
      set(
        state,
        'benguideError.invalidated',
        code === 'benefit guide invalidated'
      );
      state.inProgress = false;
    },
    setAuthShowErrorMessage: (state, { payload }) => {
      state.showErrorMessage = payload;
    },
    // UAT-3257 — "the password you just submitted was rejected", recorded on
    // the SAME object as the rest of the auth outcome rather than in a flag of
    // its own.
    //
    // The message used to hang off `showErrorMessage`, which `Login` set
    // optimistically the moment Login was pressed — before the server had
    // answered — and which `authenticateInprogress` clears on every attempt.
    // Since `PrivateRoute` swaps `Login` out for the full-screen loader the
    // instant `inProgress` goes true, whether the visitor ever saw the message
    // depended on the order two dispatches landed in either side of an
    // unmount. It is now a fact about the failure, set only once the server has
    // actually refused, and it outlives the remount because nothing but a new
    // keystroke or a new load clears it.
    passwordRejected: (state) => {
      state.benguideError = {
        ...(state.benguideError || {}),
        authFailed: true,
        invalidPassword: true,
      };
      state.inProgress = false;
    },
    // Typing is the visitor answering the message, so it goes away.
    clearPasswordRejection: (state) => {
      if (state.benguideError?.invalidPassword) {
        state.benguideError = {
          ...state.benguideError,
          invalidPassword: false,
        };
      }
    },
  },
});

export const {
  authenticateInprogress,
  authenticateFailed,
  authenticateCompleted,
  setAuthShowErrorMessage,
  passwordRejected,
  clearPasswordRejection,
} = authSlice.actions;
export default authSlice.reducer;

export function authenticate(benguideHash: string, authParams: any) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(authenticateInprogress());
      await AuthService.authenticateBenguide(benguideHash, authParams);
      dispatch(initBenguideDataByHash(benguideHash, false));
      dispatch(authenticateCompleted());
    } catch (error) {
      dispatch(authenticateFailed(error));
      // UAT-3257 — only a PUBLISHED attempt that actually carried a password
      // can be a wrong password. The same thunk runs for the anonymous probe
      // `attemptPublicAuthentication` makes (mode PUBLISHED, no password),
      // whose 4xx means "this microsite is protected", not "you typed it
      // wrong" — telling a visitor their password was incorrect before they
      // have typed one is how the old flag behaved.
      if (authParams?.mode === 'PUBLISHED' && authParams?.password) {
        dispatch(passwordRejected());
      }
    }
  };
}

export function initBenguideDataByHash(hash: string, isEditMode: boolean): any {
  return async (dispatch: Dispatch) => {
    dispatch(authenticateInprogress());
    try {
      const { data } = await AuthService.attemptPublicAuthentication(
        hash,
        AuthService.getBenguideByHash,
        hash,
        isEditMode
      );
      dispatch(authenticateCompleted());
      dispatch(initBenGuide(data));
    } catch (error: any) {
      dispatch(authenticateFailed(error));
    }
  };
}

export function initBenguideDataByToken(previewToken: string, hash: string) {
  return async (dispatch: Dispatch) => {
    try {
      const { data } =
        await AuthService.authenticateAndGetBenguideByPreviewToken(
          previewToken,
          hash
        );
      dispatch(authenticateCompleted());
      dispatch(initBenGuide(data));
    } catch (error) {
      dispatch(authenticateFailed(error));
    }
  };
}
